<template>
  <div>
    <div>
      <h2 class="text-2xl font-bold text-center -mt-4">Comments</h2>
      <div class="filters flex justify-center gap-4 mt-4">
        <label class="inline-flex items-center">
          <input type="radio" value="all" v-model="type" @change="fetchComments" />
          <span class="ml-2">All</span>
        </label>
        <label class="inline-flex items-center">
          <input type="radio" value="detractors" v-model="type" @change="fetchComments" />
          <span class="ml-2">Detractors</span>
        </label>
        <label class="inline-flex items-center">
          <input type="radio" value="passives" v-model="type" @change="fetchComments" />
          <span class="ml-2">Passives</span>
        </label>
        <label class="inline-flex items-center">
          <input type="radio" value="promoters" v-model="type" @change="fetchComments" />
          <span class="ml-2">Promoters</span>
        </label>
      </div>
      <div v-if="!comments.length" class="text-center">
        <img src="../../../../assets/img/smiley_unhappy.png" style="height: 150px; width: 150px" class="mx-auto"/>
        <p class="text-xl">No comments available</p>
      </div>
      <div v-else>
        <table class="table-comments mt-8">
          <thead>
            <tr>
              <th style="max-width: 30%">User infos</th>
              <th style="max-width: 10%">Score</th>
              <th style="max-width: 60%">Comment</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="comment in comments" :key="comment._id">
              <td style="max-width: 30%; width: 30%; flex-direction: column">
                <p> {{ new Date(comment.datecreation).toLocaleDateString() }} </p>
                <p> {{ comment.performer.nickname }} </p>
                <p> {{ comment.performer.uuid }} </p>
              </td>
              <td style="max-width: 10%">
                <span class="flex justify-center">
                  <CircleNumber :number="comment.score.toString()" :color="getCircleColor(comment.score)" :size="30" />
                </span>
              </td>
              <td style="max-width: 60%">
                <span class="flex justify-center">{{ comment.comment }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <nav aria-label="Page navigation comments">
      <ul class="inline-flex items-center -space-x-px">
        <li>
          <button
            class="block px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700"
            @click="previousPage"
            :disabled="page <= 0"
          >
            <span class="sr-only">Previous</span>
            <svg
              aria-hidden="true"
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
          </button>
        </li>
        <li v-for="pageIndex in visiblePageNumbers" :key="pageIndex">
          <button
            @click="changePage(pageIndex - 1)"
            :class="{'pagination-button': true, 'active': pageIndex - 1 === page}"
          >
            {{ pageIndex }}
          </button>
        </li>
        <li>
          <button
            class="block px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700"
            @click="nextPage"
            :disabled="page >= totalNumberOfPages - 1"
          >
            <span class="sr-only">Next</span>
            <svg
              aria-hidden="true"
              class="w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
          </button>
        </li>
        <li>
          <input
            type="number"
            v-model.number="pageNumberInput"
            min="1"
            :max="totalNumberOfPages"
            class="px-3 py-2 text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            style="max-width: 70px;"
            @keydown.enter="jumpToPage"
          />
          <button
            class="px-3 py-2 ml-2 text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-700"
            @click="jumpToPage"
          >
            Go
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CircleNumber from '../../../schema/CircleNumber.vue'

export default {
  name: 'SurveyComments',

  components: {
    CircleNumber
  },

  props: {
    id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      comments: [],
      page: 0,
      limit: 10,
      total: 0,
      type: 'all',
      maxDisplayedPages: 10,
      pageNumberInput: 1
    }
  },

  computed: {
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    }),
    totalNumberOfPages() {
      return Math.ceil(this.total / this.limit);
    },
    visiblePageNumbers() {
      const totalPages = this.totalNumberOfPages;
      const currentPage = this.page + 1;
      const maxDisplayedPages = this.maxDisplayedPages;
      const halfMaxDisplayedPages = Math.floor(maxDisplayedPages / 2);

      let startPage = currentPage - halfMaxDisplayedPages;
      let endPage = currentPage + halfMaxDisplayedPages;

      if (startPage <= 0) {
        startPage = 1;
        endPage = Math.min(totalPages, maxDisplayedPages);
      } else if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, totalPages - maxDisplayedPages + 1);
      }

      return Array(endPage - startPage + 1)
        .fill()
        .map((_, index) => startPage + index);
    },
  },

  methods: {
    ...mapActions('surveys', ['getCommentList']),

    init() {
      this.fetchComments()
    },

    fetchComments() {
      this.comments = [] // Reset the comments before fetching new ones
      this.getCommentList({ surveyId: this.id, page: this.page, limit: this.limit, type: this.type }).then(
        commentList => {
          if (!commentList) {
            return
          }
          this.comments = commentList.answers
          this.total = commentList.total
        }
      )
    },
    changePage(pageIndex) {
      this.page = pageIndex;
      this.fetchComments();
    },
    nextPage() {
      if (this.page < this.totalNumberOfPages - 1) {
        this.page++;
        this.fetchComments();
      }
    },
    previousPage() {
      if (this.page > 0) {
        this.page--;
        this.fetchComments();
      }
    },

    jumpToPage() {
      const selectedPage = this.pageNumberInput - 1;
      if (selectedPage >= 0 && selectedPage < this.totalNumberOfPages) {
        this.page = selectedPage;
        this.fetchComments();
      }
    },

    getCircleColor(number) {
      if (number < 7) {
        return 'red'
      } else if (number < 9) {
        return 'yellow'
      } else {
        return 'green'
      }
    }
  },
  mounted() {
    this.init()
  }
}
</script>

<style scoped>
.filters {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
}

.table-comments {
  width: 100%;
  border-collapse: collapse;
}

.table-comments th,
.table-comments td {
  padding: 8px;
  border-bottom: 1px solid #ddd;
}

.table-comments th {
  font-weight: bold;
}

.table-comments tbody tr:hover {
  background-color: #f5f5f5;
}

.table-comments th:first-child,
.table-comments td:first-child {
  width: 30%;
  max-width: 30%;
}

.table-comments th:nth-child(2),
.table-comments td:nth-child(2) {
  width: 10%;
  max-width: 10%;
}

.table-comments th:last-child,
.table-comments td:last-child {
  width: 60%;
  max-width: 60%;
}

.pagination {
  display: inline-flex;
  list-style: none;
  padding: 0;
  margin-top: 10px;
}

.pagination li {
  display: inline-block;
}

.pagination-button {
  display: inline-block;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  color: #4a5568;
  cursor: pointer;
  vertical-align: middle;
  line-height: 1;
}

.pagination-button.active {
  background-color: #4299e1;
  color: #fff;
}

.pagination-button[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-icon {
  display: inline-flex;
  vertical-align: middle;
  margin-right: 0.25rem;
}

</style>



// WEBPACK FOOTER //
// src/components/surveys/ui/stats/comments.vue